import React from 'react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Receipt, MoreVertical, Tag, Percent } from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import CategoryBadge from '../common/CategoryBadge';

export default function ReceiptCard({ receipt, onClick, onEdit, onDelete }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className="group bg-white rounded-xl border border-slate-100 p-4 cursor-pointer hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-300"
      onClick={onClick}
      data-testid={`receipt-card-${receipt.id}`}
    >
      <div className="flex items-start gap-4">
        {receipt.image_url ? (
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
            <img src={receipt.image_url} alt="Receipt" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center flex-shrink-0">
            <Receipt className="w-7 h-7 text-emerald-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-slate-900 truncate">{receipt.store_name}</h3>
              <p className="text-sm text-slate-500">
                {receipt.date && format(new Date(receipt.date), 'dd MMM yyyy')}
                {receipt.payment_method && <span className="ml-2 capitalize">{receipt.payment_method.replace(/_/g, ' ')}</span>}
              </p>
              <p className="text-base font-bold text-slate-900 mt-0.5">
                {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(receipt.amount)}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-slate-700 flex-shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit?.(receipt); }}>Edit</DropdownMenuItem>
                <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); onDelete?.(receipt); }}>Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <CategoryBadge category={receipt.category} size="sm" />
            {receipt.gst_type === 'gst_10' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">GST 10%</span>}
            {receipt.gst_type === 'gst_free' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">GST Free</span>}
            {receipt.tax_deductible && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"><Percent className="w-3 h-3" />Tax {receipt.tax_split_percentage}%</span>}
            {receipt.tags?.length > 0 && <span className="inline-flex items-center gap-1 text-slate-500 text-xs"><Tag className="w-3 h-3" />{receipt.tags.length}</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
